import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, FileText, FolderCheck, Bot, LogOut, User as UserIcon, Type, Contrast, Sparkles, SearchCheck, Users, Wrench, Scale } from 'lucide-react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from '../components/ui/dropdown-menu';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { useA11y } from '../context/A11yContext';
import { useLang } from '../context/LangContext';

const NAV = [
  { to: '/find-benefits', label: 'Find Benefits', icon: Search, id: 'nav-find-benefits' },
  { to: '/applications', label: 'My Applications', icon: FileText, id: 'nav-applications' },
  { to: '/documents', label: 'My Documents', icon: FolderCheck, id: 'nav-documents' },
  { to: '/assistant', label: 'AI Assistant', icon: Bot, id: 'nav-assistant' },
];

export default function TopNav() {
  const { user, logout } = useAuth();
  const { fontScale, setFontScale, highContrast, setHighContrast, plainLanguage, setPlainLanguage } = useA11y();
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [expiring, setExpiring] = React.useState(0);

  React.useEffect(() => {
    if (!user) { setExpiring(0); return; }
    const refresh = () => api.get('/documents/expiring').then(({ data }) => setExpiring(data.count)).catch(() => {});
    refresh();
    window.addEventListener('docs-changed', refresh);
    return () => window.removeEventListener('docs-changed', refresh);
  }, [user, location.pathname]);

  return (
    <header className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border" data-testid="top-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5" data-testid="brand-home-link">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-black">
            AB
          </div>
          <div className="leading-tight hidden sm:block">
            <div className="font-lexend font-bold text-base" style={{fontFamily: 'Lexend'}}>AccessBenefits</div>
            <div className="text-xs text-muted-foreground -mt-0.5">Ontario</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = location.pathname.startsWith(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.id}
                className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${active ? 'bg-primary/10 text-primary' : 'hover:bg-secondary text-foreground'}`}
              >
                <Icon className="w-4 h-4" />
                {t(n.label)}
                {n.to === '/documents' && expiring > 0 && (
                  <span className="ml-1 min-w-[20px] h-5 px-1.5 rounded-full bg-[#C26227] text-white text-[11px] font-bold flex items-center justify-center" data-testid="nav-expiring-badge" aria-label={`${expiring} documents expiring soon`}>{expiring}</span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-border overflow-hidden text-xs font-semibold" role="group" aria-label={t('Language')} data-testid="lang-toggle">
            <button onClick={() => setLang('en')} className={`px-2.5 h-9 ${lang === 'en' ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'}`} aria-pressed={lang === 'en'} data-testid="lang-en" lang="en">EN</button>
            <button onClick={() => setLang('fr')} className={`px-2.5 h-9 ${lang === 'fr' ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'}`} aria-pressed={lang === 'fr'} data-testid="lang-fr" lang="fr">FR</button>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="a11y-toolbar-toggle" className="gap-2">
                <Contrast className="w-4 h-4" /> <span className="hidden sm:inline">{t('Accessibility')}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 bg-card">
              <div className="space-y-4">
                <div>
                  <div className="text-sm font-medium mb-2 flex items-center gap-2"><Type className="w-4 h-4" /> {t('Text Size')}</div>
                  <div className="flex gap-2">
                    {[[1,'A'],[1.15,'A+'],[1.3,'A++']].map(([v, l]) => (
                      <button
                        key={v}
                        data-testid={`font-scale-${v}`}
                        onClick={() => setFontScale(v)}
                        className={`flex-1 py-2 rounded-md border ${fontScale === v ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-secondary'}`}
                      >{l}</button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <label htmlFor="hc" className="text-sm font-medium flex items-center gap-2"><Contrast className="w-4 h-4" /> {t('High Contrast')}</label>
                  <Switch id="hc" checked={highContrast} onCheckedChange={setHighContrast} data-testid="high-contrast-toggle" />
                </div>
                <div className="flex items-center justify-between">
                  <label htmlFor="pl" className="text-sm font-medium flex items-center gap-2"><Sparkles className="w-4 h-4" /> {t('Easy Read')}</label>
                  <Switch id="pl" checked={plainLanguage} onCheckedChange={setPlainLanguage} data-testid="plain-language-toggle" />
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="user-menu-trigger" className="gap-2">
                  <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                    {(user.name || user.email || '?').charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden sm:inline text-sm">{user.name || user.email}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card w-56">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/')} data-testid="menu-dashboard"><UserIcon className="w-4 h-4 mr-2" />{t('Dashboard')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/missing-benefits')} data-testid="menu-missing-benefits"><SearchCheck className="w-4 h-4 mr-2" />{t('On ODSP? What am I missing')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/partner')} data-testid="menu-partner-mode"><Users className="w-4 h-4 mr-2" />{t('Community Partner Mode')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/equipment')} data-testid="menu-equipment"><Wrench className="w-4 h-4 mr-2" />{t('Equipment Funding Finder')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/appeals')} data-testid="menu-appeals"><Scale className="w-4 h-4 mr-2" />{t('Appeal Helper')}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} data-testid="menu-logout"><LogOut className="w-4 h-4 mr-2" />{t('Sign out')}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button size="sm" onClick={() => navigate('/login')} data-testid="signin-btn" className="bg-primary hover:bg-primary/90 text-primary-foreground">{t('Sign in')}</Button>
          )}
        </div>
      </div>
    </header>
  );
}
